import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CButton, CForm, CFormInput, CFormLabel, CFormSelect } from "@coreui/react-pro";
import VisibilitySensor from "react-visibility-sensor";
import { useAlert } from "../../../../contexts/AlertContext";
import { trackLead } from "../../../../utils/tracking";
import { sendContactEmail2 } from "../../../../services/sendContactEmail2";
import { registerAgregaGh } from "../../../../services/registerAgregaGh";
import logoAgrega from "../../../assets/img/landingPageOne/sectionSix/logo 1.png";

// Chaves conforme LandingPageLeadData da API EmailSender (/sendUniversalLPEmail)
const F = {
  NOME: 'name',
  TELEFONE: 'phone',
  CPF: 'cpf',
  CIDADE: 'city',
  CAVALO: 'truckProfile',
  ANO_CAVALO: 'truckYear',
  CNH: 'cnhCategory',
  MOPP: 'mopp',
  EXPERIENCIA: 'experience',
  SIDER: 'sider',
  DISTANCIA: 'distance',
  OPERACAO: 'operation',
  FOCO: 'campaignFocus',
};

const DEFAULT_FORM_DATA = {
  [F.NOME]: '',
  [F.TELEFONE]: '',
  [F.CPF]: '',
  [F.CIDADE]: '',
  [F.CAVALO]: '',
  [F.ANO_CAVALO]: '',
  [F.CNH]: '',
  [F.MOPP]: '',
  [F.EXPERIENCIA]: '',
  [F.SIDER]: '',
  [F.DISTANCIA]: '',
  [F.OPERACAO]: '',
  [F.FOCO]: '',
  isLoadingResponse: false,
};

const REQUIRED_FIELDS = Object.values(F);

function SectionFour() {
  const [isVisible, setIsVisible] = useState(false);
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const onChange = (visibility) => {
    if (visibility) setIsVisible(true);
  };

  const formatPhoneValue = (event) => {
    const formattedPhone = event.target.value
      .replace(/\D/g, '')
      .replace(/^(\d{2})(\d)/g, '($1) $2')
      .replace(/(\d)(\d{4})$/, '$1-$2');
    setFormData(prev => ({ ...prev, [F.TELEFONE]: formattedPhone }));
    if (errors[F.TELEFONE]) {
      setErrors(prev => ({ ...prev, [F.TELEFONE]: false }));
    }
  };

  const validate = () => {
    const newErrors = {};
    REQUIRED_FIELDS.forEach(field => {
      if (!formData[field] || formData[field].trim() === '') {
        newErrors[field] = true;
      }
    });
    if (formData[F.TELEFONE] && formData[F.TELEFONE].replace(/\D/g, '').length < 10) {
      newErrors[F.TELEFONE] = true;
    }
    if (formData[F.CPF] && formData[F.CPF].length !== 11) {
      newErrors[F.CPF] = true;
    }
    if (formData[F.ANO_CAVALO] && formData[F.ANO_CAVALO].length !== 4) {
      newErrors[F.ANO_CAVALO] = true;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmit = async () => {
    if (!validate()) {
      showAlert(t("lp2_form_validation_error") || "Preencha todos os campos obrigatórios.", 'error');
      return;
    }

    setFormData(prev => ({ ...prev, isLoadingResponse: true }));

    const { isLoadingResponse, ...payload } = formData;
    // E-mail (EmailSender) e planilha do cliente (API do blog) em paralelo; o retorno ao usuário segue o e-mail.
    const [response, sheet] = await Promise.all([sendContactEmail2(payload), registerAgregaGh(payload)]);
    if (!sheet.success) console.error("Agrega+GH: cadastro não registrado na planilha", sheet.message);

    if (response.success) {
      trackLead('lp_motorista', { cidade: formData[F.CIDADE] });
      setFormData(DEFAULT_FORM_DATA);
      setErrors({});
      showAlert(t("lp2_form_success") || "Formulário enviado com sucesso!", 'success');
    } else {
      showAlert(response.message || t("lp2_form_error"), 'error');
    }

    setFormData(prev => ({ ...prev, isLoadingResponse: false }));
  };

  const invalidStyle = { borderColor: 'red' };

  return (
    <VisibilitySensor onChange={onChange} partialVisibility minTopValue={100}>
      <div id="contact" className={`sectionFour ${isVisible ? 'visible' : ''}`}>
        <div className="sectionFourText">
          <img src={logoAgrega} alt="Agrega+GH" width={180} className="logoAgrega" />
          {/* <h1 className={`${isVisible ? 'visible' : ''}`}>{t("lp2_form_title")}</h1> */}
          <h2>{t("lp2_form_subtitle")}</h2>
          <p>{t("lp2_form_text1")}</p>
          <p>{t("lp2_form_text2")}</p>
          <p className="cta">{t("lp2_form_cta")}</p>
        </div>
        <CForm id="motoristaForm" className={`${isVisible ? 'visible' : ''}`}>
          <div className="formGrid">
            <div className="formField full">
              <CFormLabel>{t("lp2_form_focus")}</CFormLabel>
              <CFormSelect
                aria-label={t("lp2_form_focus")}
                options={[
                  { label: '' },
                  { label: t("lp2_form_focus_option1"), value: 'Mercosul / Internacional' },
                  { label: t("lp2_form_focus_option2"), value: 'Rodoviário Nacional' },
                ]}
                value={formData[F.FOCO]}
                style={errors[F.FOCO] ? invalidStyle : {}}
                onChange={(e) => handleFieldChange(F.FOCO, e.target.value)}
              />
            </div>

            <div className="formField full">
              <CFormLabel className="me-2 labelName">{t("lp1_form_name")}</CFormLabel>
              <CFormInput
                type="text"
                maxLength={80}
                minLength={3}
                value={formData[F.NOME]}
                style={errors[F.NOME] ? invalidStyle : {}}
                onChange={(e) => handleFieldChange(F.NOME, e.target.value)}
                className="me-2"
                placeholder=""
              />
            </div>

            <div className="formField">
              <CFormLabel>{t("lp1_form_phone")}</CFormLabel>
              <CFormInput
                type="phone"
                autoComplete="off"
                maxLength={15}
                minLength={15}
                onChange={formatPhoneValue}
                className="me-2"
                placeholder=""
                value={formData[F.TELEFONE]}
                style={errors[F.TELEFONE] ? invalidStyle : {}}
              />
            </div>

            <div className="formField">
              <CFormLabel>{t("lp1_form_cpf")}</CFormLabel>
              <CFormInput
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={11}
                className="me-2"
                placeholder=""
                value={formData[F.CPF]}
                style={errors[F.CPF] ? invalidStyle : {}}
                onChange={(e) => handleFieldChange(F.CPF, e.target.value.replace(/\D/g, ''))}
              />
            </div>

            <div className="formField">
              <CFormLabel>{t("lp1_form_city")}</CFormLabel>
              <CFormSelect
                aria-label={t("lp1_form_city")}
                options={[
                  { label: '' },
                  { label: t("lp1_form_city_option1"), value: 'até 100 km de Itajaí' },
                  { label: t("lp1_form_city_option2"), value: 'até 100 km de Curitiba' },
                  { label: t("lp1_form_city_option3"), value: 'Interior ou outras regiões' },
                ]}
                value={formData[F.CIDADE]}
                style={errors[F.CIDADE] ? invalidStyle : {}}
                onChange={(e) => handleFieldChange(F.CIDADE, e.target.value)}
              />
            </div>

            <div className="formField">
              <CFormLabel>{t("lp2_form_cnh")}</CFormLabel>
              <CFormSelect
                aria-label={t("lp2_form_cnh")}
                options={[
                  { label: '' },
                  { label: t("lp2_form_cnh_option1"), value: 'C' },
                  { label: t("lp2_form_cnh_option2"), value: 'D' },
                  { label: t("lp2_form_cnh_option3"), value: 'E' },
                ]}
                value={formData[F.CNH]}
                style={errors[F.CNH] ? invalidStyle : {}}
                onChange={(e) => handleFieldChange(F.CNH, e.target.value)}
              />
            </div>

            <div className="formField">
              <CFormLabel>{t("lp2_form_truck")}</CFormLabel>
              <CFormSelect
                aria-label={t("lp2_form_truck")}
                options={[
                  { label: '' },
                  { label: t("lp2_form_truck_option1"), value: '6x2' },
                  { label: t("lp2_form_truck_option2"), value: '4x2' },
                ]}
                value={formData[F.CAVALO]}
                style={errors[F.CAVALO] ? invalidStyle : {}}
                onChange={(e) => handleFieldChange(F.CAVALO, e.target.value)}
              />
            </div>

            <div className="formField">
              <CFormLabel>{t("lp2_form_truck_year")}</CFormLabel>
              <CFormInput
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                className="me-2"
                placeholder=""
                value={formData[F.ANO_CAVALO]}
                style={errors[F.ANO_CAVALO] ? invalidStyle : {}}
                onChange={(e) => handleFieldChange(F.ANO_CAVALO, e.target.value.replace(/\D/g, ''))}
              />
            </div>

            <div className="formField">
              <CFormLabel>{t("lp2_form_mopp")}</CFormLabel>
              <CFormSelect
                aria-label={t("lp2_form_mopp")}
                options={[
                  { label: '' },
                  { label: t("lp2_form_mopp_option1"), value: 'Sim' },
                  { label: t("lp2_form_mopp_option2"), value: 'Não' },
                ]}
                value={formData[F.MOPP]}
                style={errors[F.MOPP] ? invalidStyle : {}}
                onChange={(e) => handleFieldChange(F.MOPP, e.target.value)}
              />
            </div>

            <div className="formField">
              <CFormLabel>{t("lp1_form_experience")}</CFormLabel>
              <CFormSelect
                aria-label={t("lp1_form_experience")}
                options={[
                  { label: '' },
                  { label: t("lp1_form_experience_option1"), value: 'Menos de 1 ano' },
                  { label: t("lp1_form_experience_option2"), value: 'Entre 1 e 2 anos' },
                  { label: t("lp1_form_experience_option3"), value: 'Mais de 2 anos' },
                ]}
                value={formData[F.EXPERIENCIA]}
                style={errors[F.EXPERIENCIA] ? invalidStyle : {}}
                onChange={(e) => handleFieldChange(F.EXPERIENCIA, e.target.value)}
              />
            </div>

            <div className="formField">
              <CFormLabel>{t("lp1_form_sider")}</CFormLabel>
              <CFormSelect
                aria-label={t("lp1_form_sider")}
                options={[
                  { label: '' },
                  { label: t("lp1_form_sider_option1"), value: 'Não tenho experiência' },
                  { label: t("lp1_form_sider_option2"), value: 'Tenho pouca experiência' },
                  { label: t("lp1_form_sider_option3"), value: 'Ampla experiência' },
                ]}
                value={formData[F.SIDER]}
                style={errors[F.SIDER] ? invalidStyle : {}}
                onChange={(e) => handleFieldChange(F.SIDER, e.target.value)}
              />
            </div>

            <div className="formField">
              <CFormLabel>{t("lp1_form_distance")}</CFormLabel>
              <CFormSelect
                aria-label={t("lp1_form_distance")}
                options={[
                  { label: '' },
                  { label: t("lp1_form_distance_option1"), value: 'Sim' },
                  { label: t("lp1_form_distance_option2"), value: 'Não' },
                ]}
                value={formData[F.DISTANCIA]}
                style={errors[F.DISTANCIA] ? invalidStyle : {}}
                onChange={(e) => handleFieldChange(F.DISTANCIA, e.target.value)}
              />
            </div>

            <div className="formField full">
              <CFormLabel>{t("lp1_form_operation")}</CFormLabel>
              <CFormSelect
                aria-label={t("lp1_form_operation")}
                options={[
                  { label: '' },
                  { label: t("lp1_form_operation_option1"), value: 'Prefiro empresas com mais liberdade' },
                  { label: t("lp1_form_operation_option2"), value: 'Nunca trabalhei com isso, mas estou disposto(a) a aprender' },
                  { label: t("lp1_form_operation_option3"), value: 'Já tive contato com alguns desses controles' },
                  { label: t("lp1_form_operation_option4"), value: 'Me sinto totalmente à vontade' },
                ]}
                value={formData[F.OPERACAO]}
                style={errors[F.OPERACAO] ? invalidStyle : {}}
                onChange={(e) => handleFieldChange(F.OPERACAO, e.target.value)}
              />
            </div>

            <CButton className="mt-4 full" onClick={handleSubmit} disabled={formData.isLoadingResponse}>
              {formData.isLoadingResponse ? t("lp2_form_sending") : t("lp2_form_send")}
            </CButton>
          </div>
        </CForm>
      </div>
    </VisibilitySensor>
  );
}

export default SectionFour;